import debounce from 'debounce';
import { postFormData } from 'modules/helpers/fetch';
import React, { useState, useRef, forwardRef } from 'react';
import styled from 'styled-components';
import { colors } from 'variables';

import DefaultInput, { type IDefaultInput, Title as DefaultTitle } from './defaultInput';

enum validState {
    notValidated = 1,
    valid = 2,
    invalid = 3
}

const Input = styled(DefaultInput)<{ state: validState }>`
    font-weight: ${({ state }): string => (state === validState.valid ? 'bold' : 'normal')};
    color: ${({ state }): string => (state === validState.invalid ? colors.error : colors.positive)};
`;

const Title = styled(DefaultTitle)<{ state: validState }>`
    color: ${({ state }): string => (state === validState.invalid ? colors.error : colors.positive)};
`;

interface IValidatedInput extends IDefaultInput {
    validateUrl: string;
    field: string;
    validationState: number;
}

export default forwardRef<HTMLInputElement, IValidatedInput>((props: IValidatedInput, ref): JSX.Element => {
    const {
        validateUrl,
        field,
        validationState: initialValidationState,
        label,
        value: initialValue = '',
        onValueChange: onValueChangeProp,
        ...rest
    } = props;
    const [text, setText] = useState(label);
    const [valid, setValid] = useState(validState.notValidated);
    const value = useRef(initialValue);
    const validationState = useRef(initialValidationState);

    const updateState = (): void => {
        postFormData<{ success: boolean; message: string }>(validateUrl, {
            field,
            value: value.current.toString()
        }).then((res): void => {
            const { success, message } = res;
            validationState.current = 2;
            setText(success ? label : message);
            setValid(success ? validState.valid : validState.invalid);
        });
    };

    const onUpdate = (isBlur = false): void => {
        const val = value.current.toString();
        const originalValue = initialValue.toString();
        // Allow validation if input have/had a value
        if (val.length >= 1 || originalValue.length >= 1) {
            if (validationState.current === 0) {
                validationState.current = 1; // allow validation on blur from now on
                if (originalValue.length >= 1) {
                    validationState.current = 2; // allow validation on keyup and blur from now on
                }
            }
        }

        if (isBlur) {
            if (validationState.current < 1) return;
            updateState();
        } else {
            if (validationState.current < 2) return;
            updateState();
        }
    };

    const debouncedUpdate = useRef(debounce(onUpdate, 250));

    const onValueChange = (val: string | number | string[]): void => {
        value.current = val;
        if (onValueChangeProp) onValueChangeProp(val);
        debouncedUpdate.current();
    };

    const onBlur = (): void => {
        onUpdate(true);
    };

    const LabelComponent = (): JSX.Element => <Title state={valid}>{text}</Title>;

    return (
        <Input
            state={valid}
            labelComponent={LabelComponent}
            {...rest}
            value={value.current}
            onValueChange={onValueChange}
            onBlur={onBlur}
            ref={ref}
        />
    );
});
